import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { formatMoney } from 'utils/storefront/format-resource';
import { Pagination } from 'swiper';
import CartService from './cart-service';

const RecommendedProductItem = ({ variant }) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const handleAddToCart = (variantId) => {
    setButtonLoading(true)
    CartService.add({
      merchandiseId: `/${variantId}`,
      quantity: 1,
    }).finally(() => setButtonLoading(false));
  };

  return (
    <div className={buttonLoading ? 'opacity-25 pointer-events-none flex flex-col h-full' : 'flex flex-col h-full'}>
      <a className="no-underline" href={variant.url}>
        <img src={variant.image.src} alt={variant.image.alt} />
      </a>
      <div className="my-3">
        <a href={variant.url} className="block no-underline product-card-name text-14 font-light">{variant?.productName}</a>
        <a href={variant.url} className="block no-underline product-card-description text-12 text-secondary">{variant?.variantName}</a>
        <span className="block text-14 font-light">
          {formatMoney({
            amount: variant.price.amount,
            currencyCode: variant.price.currencyCode || window.TMW?.localization?.currencyCode || 'USD',
          })}
        </span>
      </div>
      <button
        onClick={() => handleAddToCart(variant.id)}
        className="h-7 utility-4 text-primary bg-white bg-opacity-80 px-1.5 w-full border border-grey-3 hover:text-white hover:bg-black !leading-[11px] capitalize mt-auto">
        {buttonLoading
          ? 'Loading...'
          :  window.TMW?.Strings.cart.recommendedProducts.shopButton
        }
      </button>
    </div>
  )
}

const ProductRecommendation = (props) => {
  const { cart } = props;
  const cartItems = cart?.state?.items;
  const [recommendedVariants, setRecommendedVariants] = useState([]);
  const [fetchedProducts, setFetchedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const parseJsonFromHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return JSON.parse(doc.body.querySelector('[data-section="product"] script[data-section-data]').innerText);
  }
  const fetchRecommendedProductByHandle = async (handle) => {
    const response = await fetch(`/products/${handle}?view=json`);
    const html = await response.text();
    return parseJsonFromHtml(html);
  }
  const fetchRecommendedProducts = async (handles) => {

    if (!Array.isArray(handles)) {
      // eslint-disable-next-line no-console
      console.warn("Handles is not an array or is undefined.");
      return;
    }

    const products = await Promise.all(
      handles.map(handle => fetchRecommendedProductByHandle(handle))
    );
    
    setFetchedProducts(products);
  }

  useEffect(() => {
    const cartItemHandles = cartItems?.map((item) => item.handle);
    fetchRecommendedProducts(cartItemHandles).finally(() => setLoading(false));
  }, [cartItems]);

  useEffect(() => {
    if (!loading) {
      const styleItWithVariants = cartItems?.reduce((acc, item) => {
        const maxProductRecommendations = 4;
        const limitPerCartItem = 2;

        if (acc.length >= maxProductRecommendations) return acc;
        // See section.product.liquid for JSON data source
        const productData = fetchedProducts.find(data =>
          data.product.variants.some(v => Number(v.id.split('ProductVariant/')[1]) === item.variant_id)
        );
        if (productData) {
          const variant = productData.product.variants.find(v =>
            Number(v.id.split('ProductVariant/')[1]) === item.variant_id
          );
          const ignoreSafetyStock = productData.product.ignoreSafetyStock === 'true';

          if (variant && variant.styleItWith.length > 0) {
            const { safetyStockQuantity } = window.TMW;
            // Only add styleItWith variants that are not already in the cart (by variant_id)
            const filteredVariants = variant.styleItWith
              .filter(swv => !cartItems.some(ci => ci.variant_id === swv.id))
              .filter((styleItWithItem) =>
                (styleItWithItem.inventoryQuantity > safetyStockQuantity
                  && !ignoreSafetyStock) || ignoreSafetyStock
              )
              .slice(0, limitPerCartItem)

            acc.push(...filteredVariants);
          }
        }

        return acc;
      }, []);

      setRecommendedVariants(styleItWithVariants);
    }
  }, [fetchedProducts, loading, cartItems]);

  return (
    <div>
      {!loading && recommendedVariants?.length > 0 && (
        <div className="px-5 pt-2 pb-6">
          <p className="subhead-1 mb-4">{window.TMW?.Strings.cart.recommendedProducts.title}</p>
          <Swiper
            className="!pb-10"
            slidesPerView={2}
            spaceBetween={10}
            modules={[Pagination]}
            pagination={{ clickable: true, el: '[data-recommended-slider-pagination]' }}
          >
            {recommendedVariants.map((variant) => (
              <SwiperSlide key={variant.id} className='!h-auto'>
                <RecommendedProductItem variant={variant} />
              </SwiperSlide>
            ))}
            <div
              className='swiper-pagination custom-carousel-pagination !bottom-0'
              data-recommended-slider-pagination
            ></div>
          </Swiper>
        </div>
      )}
    </div>
  )
}

export default ProductRecommendation;
